import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect, useBrands } from '../lib/brands.js';
import { dateTime, num, pct } from '../lib/format.js';
import {
  TX_CHECK_LABEL,
  TX_MODE_LABEL,
  TX_PARITY_LABEL,
  TX_REASON_LABEL,
  TX_STATUS_LABEL,
  TX_TIMELINE_LABEL,
  TX_TIMELINE_STATE_LABEL,
  label,
} from '../lib/labels.js';
import { hrefFor } from '../lib/router.js';
import type { Rights } from '../lib/session.js';
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
  TabBar,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import { PassthroughTemplates } from './PassthroughTemplates.js';
import { TransactionalMonitor } from './TransactionalMonitor.js';
import '../screens-2b.css';

type Mode = 'off' | 'shadow' | 'live';

interface Counts {
  sent: number;
  held: number;
  failed: number;
  unknown: number;
  shadow: number;
}

interface OverviewItem {
  brand: string;
  code: string;
  name: string;
  mode: Mode;
  render: 'hub' | 'passthrough';
  switchOn: boolean;
  templatePublished: boolean;
  missingLanguages: string[];
  counts24h: Counts;
  counts7d: Counts;
  heldByReason: Record<string, number>;
  p95LagMs: number | null;
  failureRate: number;
  lastError: { at: string; status: string; reason: string | null; message: string } | null;
}

interface RequestRow {
  id: string;
  source: string;
  acceptedAt: string;
  brand: string;
  code: string;
  recipient: string;
  orderRef: string | null;
  status: string;
  reason: string | null;
  lagMs: number | null;
}

interface RequestDetail extends RequestRow {
  name: string;
  language: string;
  render: string;
  recipientName: string | null;
  shopShouldSend: boolean;
  attempts: number;
  validUntil: string | null;
  sendAfter: string | null;
  sentAt: string | null;
  lastError: string | null;
  redacted: boolean;
  decisions: Array<{ outcome: string; reason: string; at: string }>;
  reactions: Record<string, { at: string } | null>;
  dataKeys: string[];
  source: string;
  timeline: Array<{
    key: string;
    at: string | null;
    state: 'done' | 'pending' | 'failed' | 'skipped';
    reason?: string | null;
    note?: string | null;
  }>;
}

interface Check {
  key: string;
  ok: boolean;
  detail: string;
}

interface Parity {
  matched: number;
  onlyShop: number;
  onlyHub: number;
  both: number;
  unmatchable: boolean;
  reason?: string;
  items: Array<{ kind: string; at: string; requestId: string | null; orderRef: string | null }>;
}

type TabId = 'monitor' | 'health' | 'requests' | 'parity' | 'templates';

const STATUS_TONE: Record<string, Tone> = {
  sent: 'ok',
  held: 'warn',
  failed: 'bad',
  unknown: 'bad',
  shadow: 'sim',
  dry_run: 'sim',
  queued: 'neu',
  sending: 'neu',
  cancelled: 'neu',
};

const lag = (ms: number | null): string =>
  ms === null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1).replace('.', ',')} s`;

function StatusPill({ status }: { status: string }): JSX.Element {
  return <Pill tone={STATUS_TONE[status] ?? 'neu'}>{label(TX_STATUS_LABEL, status)}</Pill>;
}

/** Obrazovka „Transakční zprávy z e-shopu“ (D-205): zdraví kódů, zprávy a parita se stínem. */
export function Transactional({
  rights,
  param,
}: {
  rights: Rights;
  param?: string | undefined;
}): JSX.Element {
  const [tab, setTab] = useState<TabId>(param ? 'requests' : 'monitor');
  const [openRequest, setOpenRequest] = useState<string | null>(param ?? null);
  const showRequest = (id: string): void => {
    setOpenRequest(id);
    setTab('requests');
  };
  return (
    <div className="stack">
      <PageHeader
        title="Transakční zprávy z e-shopu"
        description="Potvrzení objednávek, vstupenek a další servisní e-maily, které požaduje e-shop. Tady vidíte, jestli je hub umí poslat místo e-shopu, a co se s každou zprávou stalo."
      />
      <TabBar<TabId>
        value={tab}
        onChange={setTab}
        tabs={[
          {
            id: 'monitor',
            label: 'Sledování',
            hint: 'Počty, prodleva, zaseknuté, žádosti o heslo',
            icon: 'chart',
          },
          {
            id: 'health',
            label: 'Zdraví kódů',
            hint: 'Režim, šablona, výsledky a chyby',
            icon: 'chart',
          },
          {
            id: 'requests',
            label: 'Zprávy',
            hint: 'Co e-shop poslal a co se s tím stalo',
            icon: 'mail',
          },
          {
            id: 'parity',
            label: 'Parita',
            hint: 'Stín proti tomu, co posílá e-shop',
            icon: 'refresh',
          },
          {
            id: 'templates',
            label: 'Šablony e-shopu',
            hint: 'Čísla e-mailů ze SmartEmailingu pro průchod',
            icon: 'file',
          },
        ]}
      />
      {tab === 'monitor' ? <TransactionalMonitor /> : null}
      {tab === 'health' ? <Health rights={rights} /> : null}
      {tab === 'requests' ? (
        <Requests rights={rights} openId={openRequest} onOpen={setOpenRequest} />
      ) : null}
      {tab === 'parity' ? <ParityTab onOpen={showRequest} /> : null}
      {tab === 'templates' ? <PassthroughTemplates rights={rights} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ Zdraví kódů -- */

function Health({ rights }: { rights: Rights }): JSX.Element {
  const [brand, setBrand] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<{ brand: string; code: string } | null>(null);
  const client = useQueryClient();
  const canWrite = rights.write('transactional');
  const query = useQuery<{ items: OverviewItem[] }>({
    queryKey: ['tx-overview', brand],
    queryFn: () => api.get(`/api/transactional/overview${qs({ brand })}`),
  });
  const setMode = useMutation({
    mutationFn: (input: {
      brand: string;
      code: string;
      mode: Mode;
      render?: 'hub' | 'passthrough';
      force?: boolean;
      reason?: string;
    }) =>
      api.put(`/api/transactional/modes/${input.brand}/${input.code}`, {
        mode: input.mode,
        ...(input.render ? { render: input.render } : {}),
        ...(input.force ? { force: true, reason: input.reason } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setAsking(null);
      void client.invalidateQueries({ queryKey: ['tx-overview'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <Loading what="přehled kódů" />;
  if (query.isError)
    return (
      <ErrorState
        title="Přehled kódů se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const items = query.data?.items ?? [];

  return (
    <Panel
      title="Kódy a režimy"
      subtitle="Výchozí režim je Vypnuto. Ve stínu hub zprávu jen vykreslí a porovná, e-shop posílá dál sám."
      action={<BrandSelect value={brand} onChange={setBrand} />}
    >
      <div className="help">
        <p>
          <b>Vypnuto</b>: hub nic nedělá, zprávy posílá e-shop. <b>Stín</b>: hub každou zprávu jen
          vykreslí a porovná s e-shopem, nic neodesílá. <b>Ostré</b>: zprávu odešle hub a zákazníci
          ji dostanou od něj. Na ostré se přepíná až po splnění kontrol.
        </p>
      </div>
      {error ? <ErrorState title="Režim se nepodařilo změnit" error={new Error(error)} /> : null}
      {items.length === 0 ? (
        <EmptyState title="Žádné kódy zpráv">
          Pro vybranou značku nejsou nastavené žádné transakční zprávy.
        </EmptyState>
      ) : null}
      <div className="scroll">
        <table data-testid="tx-health-table">
          <thead>
            <tr>
              <th>Značka</th>
              <th>Kód</th>
              <th>Režim</th>
              <th>Smí odejít</th>
              <th>Šablona</th>
              <th>Za 24 h (odesláno / zadrženo / chyby / neznámé / stín)</th>
              <th className="n">Prodleva p95</th>
              <th>Poslední chyba</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const c = item.counts24h;
              return (
                <tr
                  key={`${item.brand}|${item.code}`}
                  className={item.failureRate >= 0.2 ? 'rowbad' : undefined}
                  data-testid={`tx-row-${item.brand}-${item.code}`}
                >
                  <td>{item.brand}</td>
                  <td>
                    <b>{item.code}</b>
                    <div className="muted">{item.name}</div>
                  </td>
                  <td>
                    <div className="seg" data-testid={`tx-mode-${item.brand}-${item.code}`}>
                      {(['off', 'shadow', 'live'] as Mode[]).map((m) => (
                        <button
                          key={m}
                          className={item.mode === m ? 'on' : ''}
                          aria-pressed={item.mode === m}
                          disabled={!canWrite || setMode.isPending}
                          data-testid={`tx-mode-${item.brand}-${item.code}-${m}`}
                          onClick={() => {
                            if (item.mode === m) return;
                            if (m === 'live') {
                              setAsking({ brand: item.brand, code: item.code });
                            } else {
                              setAsking(null);
                              setMode.mutate({ brand: item.brand, code: item.code, mode: m });
                            }
                          }}
                        >
                          {TX_MODE_LABEL[m]}
                        </button>
                      ))}
                    </div>
                    <label className="note s2b-block">
                      Skládá{' '}
                      <select
                        value={item.render}
                        disabled={!canWrite || item.mode === 'live' || setMode.isPending}
                        aria-label={`Způsob skládání ${item.code} ${item.brand}`}
                        data-testid={`tx-render-${item.brand}-${item.code}`}
                        onChange={(e) =>
                          setMode.mutate({
                            brand: item.brand,
                            code: item.code,
                            mode: item.mode,
                            render: e.target.value as 'hub' | 'passthrough',
                          })
                        }
                      >
                        <option value="hub">hub (šablona z editoru)</option>
                        <option value="passthrough">šablona e-shopu</option>
                      </select>
                    </label>
                  </td>
                  <td>
                    <Pill tone={item.switchOn ? 'ok' : 'warn'}>
                      {item.switchOn ? 'zapnuto' : 'vypnuto'}
                    </Pill>{' '}
                    <a href={hrefFor('communication')}>Komunikace</a>
                  </td>
                  <td>
                    {item.templatePublished ? (
                      <Pill tone="ok">✓ publikovaná</Pill>
                    ) : (
                      <>
                        <Pill tone="warn">chybí {item.missingLanguages.join(', ')}</Pill>{' '}
                        <a href={hrefFor('communication')}>Editor šablon</a>
                      </>
                    )}
                  </td>
                  <td className="n">
                    {num(c.sent)} / {num(c.held)} / {num(c.failed)} / {num(c.unknown)} /{' '}
                    {num(c.shadow)}
                    {c.sent + c.failed + c.unknown > 0 ? (
                      <div className="muted">
                        chybovost {pct(Math.round(item.failureRate * 100))}
                      </div>
                    ) : null}
                    {Object.keys(item.heldByReason).length > 0 ? (
                      <div className="muted">
                        {Object.entries(item.heldByReason)
                          .map(([reason, n]) => `${label(TX_REASON_LABEL, reason)}: ${n}`)
                          .join(' · ')}
                      </div>
                    ) : null}
                  </td>
                  <td className="n">{lag(item.p95LagMs)}</td>
                  <td>
                    {item.lastError ? (
                      <>
                        <div>{item.lastError.message}</div>
                        <div className="muted">{dateTime(item.lastError.at)}</div>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {asking ? (
        <CutoverDialog
          brand={asking.brand}
          code={asking.code}
          rights={rights}
          busy={setMode.isPending}
          onCancel={() => setAsking(null)}
          onConfirm={(force, reason) =>
            setMode.mutate({ ...asking, mode: 'live', ...(force ? { force, reason } : {}) })
          }
        />
      ) : null}
    </Panel>
  );
}

function CutoverDialog({
  brand,
  code,
  rights,
  busy,
  onCancel,
  onConfirm,
}: {
  brand: string;
  code: string;
  rights: Rights;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (force: boolean, reason: string) => void;
}): JSX.Element {
  const client = useQueryClient();
  const [force, setForce] = useState(false);
  const [reason, setReason] = useState('');
  const query = useQuery<{ ready: boolean; checks: Check[] }>({
    queryKey: ['tx-cutover', brand, code],
    queryFn: () => api.get(`/api/transactional/cutover-check${qs({ brand, code })}`),
  });
  const approve = useMutation({
    mutationFn: () => api.post(`/api/transactional/cutover/${brand}/${code}/approve-samples`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['tx-cutover', brand, code] }),
  });
  const mayLive = rights.can('toggle_send_switch');
  const mayForce = rights.departments.includes('admin');
  const ready = query.data?.ready === true;
  const ok = mayLive && (ready || (force && mayForce && reason.trim().length >= 3));

  return (
    <Dialog
      open
      wide
      title={`Přepnout ${code} (${brand}) na ostrý provoz?`}
      role="alertdialog"
      testId="tx-cutover"
      onClose={onCancel}
    >
      <p>
        <b>Zprávy začnou odcházet skutečným zákazníkům.</b> Ostrý provoz jde zapnout, až když
        projdou všechny kontroly níže.
      </p>
      {query.isLoading ? <Loading what="kontroly" /> : null}
      {query.isError ? (
        <ErrorState
          title="Kontroly se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      <ul className="tx-checks">
        {(query.data?.checks ?? []).map((c) => (
          <li key={c.key} data-testid={`tx-check-${c.key}`}>
            <Pill tone={c.ok ? 'ok' : 'bad'}>{c.ok ? 'splněno' : 'nesplněno'}</Pill>{' '}
            <b>{label(TX_CHECK_LABEL, c.key)}</b> — {c.detail}
          </li>
        ))}
      </ul>
      {!ready && rights.write('transactional') ? (
        <div className="row">
          <button className="btn" disabled={approve.isPending} onClick={() => approve.mutate()}>
            Schválil jsem ukázky
          </button>
          <span className="note">
            Ukázky zpráv zkontrolujte v Komunikaci → Test odesílání, pak je schvalte tady.
          </span>
        </div>
      ) : null}
      {!mayLive ? (
        <p className="note">
          Ostrý režim smí zapnout jen ten, kdo smí přepínat „smí odejít“ (administrátor, e-mailing).
        </p>
      ) : null}
      {!ready && mayLive && mayForce ? (
        <>
          <label className="row">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            <span>Obejít nesplněné podmínky (jen administrátor, zapíše se do auditu)</span>
          </label>
          {force ? (
            <label className="field">
              <span>Důvod obejití</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          ) : null}
        </>
      ) : null}
      <div className="row">
        <button
          className="btn dng"
          disabled={!ok || busy}
          data-testid="tx-cutover-confirm"
          onClick={() => onConfirm(!ready, reason.trim())}
        >
          Potvrdit
        </button>
        <button className="btn" onClick={onCancel}>
          Zrušit
        </button>
      </div>
    </Dialog>
  );
}

/* --------------------------------------------------------------- Zprávy ---- */

function Requests({
  rights,
  openId,
  onOpen,
}: {
  rights: Rights;
  openId: string | null;
  onOpen: (id: string | null) => void;
}): JSX.Element {
  const [brand, setBrand] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [order, setOrder] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState('');
  const [recipient, setRecipient] = useState('');
  const [searched, setSearched] = useState('');
  const [offset, setOffset] = useState(0);
  const query = useQuery<{ total: number; items: RequestRow[] }>({
    queryKey: ['tx-requests', brand, code, status, order, from, to, source, searched, offset],
    queryFn: () => {
      const filter = {
        brand,
        code,
        status,
        order,
        source,
        from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
        to: to ? new Date(`${to}T23:59:59`).toISOString() : '',
        offset,
      };
      // Hledaná adresa jde tělem požadavku, aby nebyla v adrese URL ani v logu.
      if (searched.length >= 3) {
        const body = Object.fromEntries(
          Object.entries({ ...filter, recipient: searched }).filter(([, v]) => v !== '' && v !== 0),
        );
        return api.post<{ total: number; items: RequestRow[] }>(
          '/api/transactional/requests/search',
          body,
        );
      }
      return api.get<{ total: number; items: RequestRow[] }>(
        `/api/transactional/requests${qs(filter)}`,
      );
    },
  });
  const reset = (fn: () => void): void => {
    fn();
    setOffset(0);
  };

  return (
    <>
      <Panel
        title="Zprávy z e-shopu"
        action={<BrandSelect value={brand} onChange={(b) => reset(() => setBrand(b))} />}
      >
        <div className="row s2b-filters">
          <label className="field">
            <span>Kód</span>
            <input
              value={code}
              placeholder="T01"
              onChange={(e) => reset(() => setCode(e.target.value.toUpperCase()))}
            />
          </label>
          <label className="field">
            <span>Stav</span>
            <select value={status} onChange={(e) => reset(() => setStatus(e.target.value))}>
              <option value="">Všechny</option>
              {Object.entries(TX_STATUS_LABEL).map(([key, text]) => (
                <option key={key} value={key}>
                  {text}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Objednávka</span>
            <input value={order} onChange={(e) => reset(() => setOrder(e.target.value))} />
          </label>
          <label className="field">
            <span>Zdroj</span>
            <input
              value={source}
              placeholder="eshop-tx"
              onChange={(e) => reset(() => setSource(e.target.value.trim()))}
            />
          </label>
          <label className="field">
            <span>Příjemce (část adresy, od 3 znaků, Enter)</span>
            <input
              type="search"
              value={recipient}
              autoComplete="off"
              onChange={(e) => {
                setRecipient(e.target.value);
                if (e.target.value.trim() === '') reset(() => setSearched(''));
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') reset(() => setSearched(recipient.trim()));
              }}
            />
          </label>
          <label className="field">
            <span>Od</span>
            <input
              type="date"
              value={from}
              onChange={(e) => reset(() => setFrom(e.target.value))}
            />
          </label>
          <label className="field">
            <span>Do</span>
            <input type="date" value={to} onChange={(e) => reset(() => setTo(e.target.value))} />
          </label>
        </div>
        {query.isLoading ? <Loading what="zprávy" /> : null}
        {query.isError ? (
          <ErrorState
            title="Zprávy se nepodařilo načíst"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : null}
        {query.data && query.data.items.length === 0 ? (
          <EmptyState title="Nic nenalezeno">
            Žádné zprávy neodpovídají filtru. Zkuste ho zrušit nebo rozšířit období.
          </EmptyState>
        ) : null}
        {query.data && query.data.items.length > 0 ? (
          <div className="scroll">
            <table data-testid="tx-requests-table">
              <thead>
                <tr>
                  <th>Čas</th>
                  <th>Značka</th>
                  <th>Kód</th>
                  <th>Zdroj</th>
                  <th>Příjemce</th>
                  <th>Objednávka</th>
                  <th>Stav</th>
                  <th className="n">Prodleva</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((r) => (
                  <tr key={r.id} className={openId === r.id ? 'sel' : ''}>
                    <td>
                      <button
                        className="click"
                        onClick={() => onOpen(openId === r.id ? null : r.id)}
                      >
                        {dateTime(r.acceptedAt)}
                      </button>
                    </td>
                    <td>{r.brand}</td>
                    <td>
                      <code>{r.code}</code>
                    </td>
                    <td>{r.source}</td>
                    <td>{r.recipient}</td>
                    <td>{r.orderRef ?? '—'}</td>
                    <td>
                      <StatusPill status={r.status} />
                      {r.reason ? (
                        <div className="muted">{label(TX_REASON_LABEL, r.reason)}</div>
                      ) : null}
                    </td>
                    <td className="n">{lag(r.lagMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {query.data ? (
          <div className="row">
            <button
              className="btn"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 50))}
            >
              Novější
            </button>
            <span className="note">
              {num(offset + 1)}–{num(Math.min(offset + 50, query.data.total))} z{' '}
              {num(query.data.total)}
            </span>
            <button
              className="btn"
              disabled={offset + 50 >= query.data.total}
              onClick={() => setOffset(offset + 50)}
            >
              Starší
            </button>
          </div>
        ) : null}
      </Panel>
      {openId ? <Detail id={openId} rights={rights} onClose={() => onOpen(null)} /> : null}
    </>
  );
}

function Detail({
  id,
  rights,
  onClose,
}: {
  id: string;
  rights: Rights;
  onClose: () => void;
}): JSX.Element {
  const client = useQueryClient();
  const canWrite = rights.write('transactional');
  const [showPreview, setShowPreview] = useState(false);
  const [resending, setResending] = useState(false);
  const [askCancel, setAskCancel] = useState(false);
  const [reason, setReason] = useState('');
  const [risk, setRisk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = useQuery<RequestDetail>({
    queryKey: ['tx-detail', id],
    queryFn: () => api.get(`/api/transactional/requests/${id}`),
  });
  const preview = useQuery<{ html: string; templateVersion: number; current: boolean }>({
    queryKey: ['tx-preview', id],
    queryFn: () => api.get(`/api/transactional/requests/${id}/preview`),
    enabled: showPreview && canWrite,
    retry: false,
  });
  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['tx-requests'] });
    void client.invalidateQueries({ queryKey: ['tx-detail', id] });
    void client.invalidateQueries({ queryKey: ['tx-overview'] });
  };
  const resend = useMutation({
    mutationFn: () =>
      api.post(`/api/transactional/requests/${id}/resend`, {
        reason: reason.trim(),
        ...(risk ? { confirmDuplicateRisk: true } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setResending(false);
      setReason('');
      setRisk(false);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/transactional/requests/${id}/cancel`),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <Loading what="detail zprávy" />;
  if (query.isError || !query.data)
    return (
      <ErrorState
        title="Detail zprávy se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const d = query.data;
  const mayResend =
    canWrite &&
    !d.redacted &&
    (d.status === 'failed' || d.status === 'unknown' || d.status === 'held');

  return (
    <Panel
      title={`Zpráva ${d.code} — ${d.name}`}
      action={
        <button className="btn" onClick={onClose}>
          Zavřít
        </button>
      }
    >
      <div className="tx-detail" data-testid="tx-detail">
        {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
        <dl>
          <dt>Stav</dt>
          <dd>
            <StatusPill status={d.status} /> {d.reason ? label(TX_REASON_LABEL, d.reason) : null}
          </dd>
          <dt>Zdroj</dt>
          <dd>{d.source}</dd>
          <dt>Příjemce</dt>
          <dd>
            {d.recipient}
            {d.recipientName ? ` (${d.recipientName})` : ''}
          </dd>
          <dt>Značka, jazyk</dt>
          <dd>
            {d.brand}, {d.language}
          </dd>
          <dt>Objednávka</dt>
          <dd>{d.orderRef ?? '—'}</dd>
          <dt>Přijato</dt>
          <dd>{dateTime(d.acceptedAt)}</dd>
          <dt>Platí do</dt>
          <dd>{dateTime(d.validUntil)}</dd>
          <dt>Odesláno</dt>
          <dd>{dateTime(d.sentAt)}</dd>
          <dt>Pokusů</dt>
          <dd>{d.attempts}</dd>
          <dt>E-shop má poslat sám</dt>
          <dd>{d.shopShouldSend ? 'ano' : 'ne'}</dd>
          {d.lastError ? (
            <>
              <dt>Chyba</dt>
              <dd>{d.lastError}</dd>
            </>
          ) : null}
          <dt>Data</dt>
          <dd>{d.redacted ? 'smazána po lhůtě uložení' : d.dataKeys.join(', ') || '—'}</dd>
        </dl>

        <div>
          <h3>Časová osa</h3>
          <ol className="tx-timeline" data-testid="tx-timeline">
            {d.timeline.map((step) => (
              <li key={step.key} className={`tl-${step.state}`}>
                <span className="tl-when">{step.at ? dateTime(step.at) : '—'}</span>
                <b>{label(TX_TIMELINE_LABEL, step.key)}</b>
                <span className="tl-state">{label(TX_TIMELINE_STATE_LABEL, step.state)}</span>
                {step.reason ? (
                  <span className="muted"> — {label(TX_REASON_LABEL, step.reason)}</span>
                ) : null}
                {step.note ? <div className="muted">{step.note}</div> : null}
              </li>
            ))}
          </ol>
          <h3>Rozhodnutí hubu</h3>
          {d.decisions.length === 0 ? (
            <p className="note">Zatím bez rozhodnutí.</p>
          ) : (
            <ul>
              {d.decisions.map((x, i) => (
                <li key={i}>
                  {dateTime(x.at)} —{' '}
                  {label(TX_STATUS_LABEL, x.outcome === 'held' ? 'held' : x.outcome)}:{' '}
                  {label(TX_REASON_LABEL, x.reason)}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canWrite && !d.redacted && d.render === 'hub' ? (
          <div>
            <button className="btn" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? 'Skrýt náhled' : 'Náhled'}
            </button>
            {showPreview ? (
              preview.isLoading ? (
                <Loading what="náhled" />
              ) : preview.isError ? (
                <ErrorState title="Náhled se nepodařilo načíst" error={preview.error} />
              ) : (
                <>
                  <p className="note">
                    Aktuální šablona, verze {preview.data?.templateVersion}. Odeslaná zpráva mohla
                    vypadat jinak, pokud se šablona od té doby změnila.
                  </p>
                  <iframe
                    title="Náhled zprávy"
                    sandbox=""
                    className="preview s2b-frame"
                    srcDoc={preview.data?.html ?? ''}
                  />
                </>
              )
            ) : null}
          </div>
        ) : null}

        <div className="row">
          {mayResend ? (
            <button className="btn" onClick={() => setResending(!resending)}>
              Poslat znovu
            </button>
          ) : null}
          {canWrite && d.status === 'queued' ? (
            <button
              className="btn dng"
              disabled={cancel.isPending}
              onClick={() => setAskCancel(true)}
            >
              Zrušit zprávu
            </button>
          ) : null}
        </div>
        <ConfirmDialog
          open={askCancel}
          danger
          title="Zrušit zprávu ve frontě?"
          confirmLabel="Zrušit zprávu"
          busy={cancel.isPending}
          onCancel={() => setAskCancel(false)}
          onConfirm={() => {
            setAskCancel(false);
            cancel.mutate();
          }}
        >
          <p>Zpráva ještě neodešla a po zrušení už se neodešle. Zpět to nejde.</p>
        </ConfirmDialog>
        <Dialog
          open={resending}
          role="alertdialog"
          title="Poslat zprávu znovu novým požadavkem?"
          onClose={() => setResending(false)}
        >
          <p>
            Vznikne nový požadavek a zpráva odejde znovu. Jestli opravdu dorazí zákazníkovi, určuje
            režim odesílání; při „Nanečisto“ se jen zapíše.
          </p>
          <div>
            <label className="field">
              <span>Důvod (povinný, zapíše se do auditu)</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            {d.status === 'unknown' ? (
              <label className="row">
                <input type="checkbox" checked={risk} onChange={(e) => setRisk(e.target.checked)} />
                <span>Chápu, že hrozí dvojí e-mail (výsledek původního odeslání není znám).</span>
              </label>
            ) : null}
            <div className="row">
              <button
                className="btn pri"
                disabled={
                  reason.trim().length < 3 || (d.status === 'unknown' && !risk) || resend.isPending
                }
                onClick={() => resend.mutate()}
              >
                Poslat znovu
              </button>
              <button className="btn" onClick={() => setResending(false)}>
                Zrušit
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    </Panel>
  );
}

/* ---------------------------------------------------------------- Parita --- */

function ParityTab({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const [chosenBrand, setBrand] = useState('');
  const [code, setCode] = useState('T01');
  const [days, setDays] = useState(7);
  const brands = useBrands();
  const brand = chosenBrand || (brands.data?.items.find((b) => b.kind === 'shop')?.code ?? '');
  const overview = useQuery<{ items: OverviewItem[] }>({
    queryKey: ['tx-overview', ''],
    queryFn: () => api.get('/api/transactional/overview'),
  });
  const codes = [...new Set((overview.data?.items ?? []).map((i) => i.code))];
  const query = useQuery<Parity>({
    queryKey: ['tx-parity', brand, code, days],
    queryFn: () => api.get(`/api/transactional/parity${qs({ brand, code, days })}`),
    enabled: brand !== '' && code !== '',
  });
  const p = query.data;

  return (
    <Panel
      title="Parita se stínem"
      subtitle="Porovnává, co by poslal hub ve stínovém režimu, s tím, co ve stejné době opravdu poslal e-shop. Hodnota „Poslalo obojí“ znamená dvojí e-mail zákazníkovi."
      action={<BrandSelect value={brand} onChange={setBrand} allLabel={null} shopOnly />}
    >
      <div className="row s2b-filters">
        <label className="field">
          <span>Kód</span>
          <select value={code} onChange={(e) => setCode(e.target.value)}>
            {codes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Období</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 3, 7, 14, 30].map((n) => (
              <option key={n} value={n}>
                {n} dní
              </option>
            ))}
          </select>
        </label>
      </div>
      {query.isLoading ? <Loading what="paritu" /> : null}
      {query.isError ? (
        <ErrorState
          title="Paritu se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {p?.unmatchable ? (
        <div className="confirm" role="alert">
          <b>Paritu nejde změřit</b>
          <span>{p.reason}</span>
        </div>
      ) : null}
      {p && !p.unmatchable ? (
        <div className="tiles" data-testid="tx-parity">
          <Tile label="Shoda" value={num(p.matched)} />
          <Tile label="Jen e-shop" value={num(p.onlyShop)} bad={p.onlyShop > 0} />
          <Tile label="Jen hub" value={num(p.onlyHub)} bad={p.onlyHub > 0} />
          <Tile label="Poslalo obojí (dvojí e-mail)" value={num(p.both)} bad={p.both > 0} />
        </div>
      ) : null}
      {p && p.items.filter((i) => i.kind !== 'matched').length > 0 ? (
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Čas</th>
                <th>Rozdíl</th>
                <th>Objednávka</th>
                <th>Zpráva</th>
              </tr>
            </thead>
            <tbody>
              {p.items
                .filter((i) => i.kind !== 'matched')
                .map((i, n) => (
                  <tr key={n}>
                    <td>{dateTime(i.at)}</td>
                    <td>
                      <Pill tone={i.kind === 'both' ? 'bad' : 'warn'}>
                        {label(TX_PARITY_LABEL, i.kind)}
                      </Pill>
                    </td>
                    <td>{i.orderRef ?? '—'}</td>
                    <td>
                      {i.requestId ? (
                        <button className="click" onClick={() => onOpen(i.requestId!)}>
                          Detail
                        </button>
                      ) : (
                        <span className="muted">jen e-shop</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Panel>
  );
}
